import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Upload, Loader2, FileCheck2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import PartyCombobox from "@/components/PartyCombobox";

function TagInput({ value = [], onChange }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Type a tag, press Enter" data-testid="tag-input" />
        <Button type="button" variant="secondary" onClick={add} data-testid="tag-add-btn">Add</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {value.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1">
            {t}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== t))}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function FileField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ storage_path: data.storage_path, original_filename: data.original_filename, content_type: data.content_type });
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-input bg-secondary text-sm cursor-pointer hover:bg-accent transition-colors" data-testid={testid}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {value ? "Replace file" : "Choose file"}
        <input type="file" className="hidden" onChange={handle} disabled={uploading}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
      </label>
      {value?.storage_path && (
        <a href={fileUrl(value.storage_path)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-primary underline max-w-[180px] truncate">
          <FileCheck2 className="h-4 w-4 shrink-0" />{value.original_filename}
        </a>
      )}
    </div>
  );
}

export default function ResourceForm({ open, onOpenChange, module, record, onSaved }) {
  const [form, setForm] = useState({});
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const init = {};
      module.fields.forEach((f) => {
        let dflt = f.type === "tags" ? [] : f.default ?? "";
        if (!record && ((f.type === "date" && f.name === "date") || f.defaultToday)) dflt = new Date().toISOString().slice(0, 10);
        init[f.name] = record?.[f.name] ?? dflt;
      });
      setForm(init);
      api.get("/resource/projects").then((r) => setProjects(r.data)).catch(() => {});
    }
  }, [open, record, module]);

  const setVal = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const missing = module.fields.filter((f) => f.required && !form[f.name]);
    if (missing.length) { toast.error(`${missing[0].label} is required`); return; }
    setSaving(true);
    try {
      const { data } = record?.id ? await api.put(`/resource/${module.key}/${record.id}`, form) : await api.post(`/resource/${module.key}`, form);
      toast.success(`${module.singular} saved`);
      onSaved?.(data, !record?.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="resource-form-sheet">
        <SheetHeader className="px-6 py-4 border-b sticky top-0 bg-card/90 backdrop-blur-md z-10">
          <SheetTitle className="font-display text-xl">
            {record?.id ? `Edit ${module.singular}` : `New ${module.singular}`}
          </SheetTitle>
        </SheetHeader>
        <div className="px-6 py-5 space-y-4">
          {module.fields.filter((f) => !f.showIf || Object.entries(f.showIf).every(([k, v]) => form[k] === v)).map((f) => { const locked = !!(f.lockedIf && record?.[f.lockedIf]); return (
            <div key={f.name} className="space-y-1.5">
              <Label className="overline text-muted-foreground">{f.label}{f.required && " *"}</Label>
              {f.hint && !record?.[f.lockedIf] && <p className="text-xs text-muted-foreground" data-testid={`hint-${f.name}`}>{f.hint}</p>}
              {locked && f.hint && <p className="text-xs text-muted-foreground" data-testid={`locked-${f.name}`}>Locked — receipt {record.money_receipt_number} already issued.</p>}
              {f.type === "textarea" ? (
                <Textarea value={form[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} rows={3} disabled={locked} data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => setVal(f.name, v)} disabled={locked}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o} value={o} data-testid={`option-${f.name}-${o}`}>{f.optionLabels?.[o] || o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "project" ? (
                <Select value={form[f.name] || "none"} onValueChange={(v) => setVal(f.name, v === "none" ? "" : v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "party" && locked ? (
                <Input value={form[f.name] || ""} disabled data-testid={`field-${f.name}`} />
              ) : f.type === "party" ? (
                <PartyCombobox value={form[f.name] || ""} onChange={(v) => setVal(f.name, v)} testid={`field-${f.name}`} />
              ) : f.type === "tags" ? (
                <TagInput value={form[f.name] || []} onChange={(v) => setVal(f.name, v)} />
              ) : f.type === "file" ? (
                <FileField value={form[f.name]} onChange={(v) => setVal(f.name, v)} testid={`field-${f.name}`} />
              ) : f.type === "readonly" ? (
                <Input value={form[f.name] || "—"} disabled data-testid={`field-${f.name}`} />
              ) : f.type === "history" ? (
                <div className="rounded-md border border-border bg-secondary/40 p-3" data-testid={`field-${f.name}`}>
                  {(!form[f.name] || form[f.name].length === 0) ? (
                    <p className="text-xs text-muted-foreground">No previous rates recorded yet. History is saved automatically when the amount changes.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {[...form[f.name]].reverse().map((h, idx) => (
                        <li key={idx} className="flex items-center justify-between text-xs">
                          <span className="font-medium">₹{h.amount}</span>
                          <span className="text-muted-foreground">{h.effective_date || "—"} · {h.changed_at ? new Date(h.changed_at).toLocaleDateString() : ""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  value={form[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} disabled={locked} data-testid={`field-${f.name}`} />
              )}
            </div>
          ); })}
        </div>
        <div className="px-6 py-4 border-t sticky bottom-0 bg-card/90 backdrop-blur-md flex gap-3">
          <Button onClick={save} disabled={saving} className="flex-1 active:scale-95 transition-transform" data-testid="save-resource-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save {module.singular}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-resource-btn">Cancel</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
